import { useEffect, useState } from 'react'
import { ScrollView, View } from 'react-native'
import Typography from '@ca/ui/typography'
import Button from '@ca/ui/buttons/Button'
import Input from '@ca/ui/forms/Input'
import { PanelBox } from './PanelBox'
import { ProgressBar } from './ProgressBar'
import { Row } from './Row'
import { Phase } from './types'
import { fmt } from './utils'
import type { ShDebugSummary } from './useSHCapture'
import { useShFaceData } from './faceDataStore'
import type {
  ShDebug,
  GlassesPresenceResult,
  SHContourQualityEyeDebug,
  SHQualityDebug,
  ShScaleDebug,
  OpticalMeasurePointAdjustmentState,
  OpticalMeasurePointSide,
  OpticalMeasureSHEstimateFormula,
} from '../index'

type Props = {
  phase: Phase
  capturedCount: number
  targetCount: number
  progress: number
  onStart: () => void
  onStop: () => void
  onClear: () => void
  onRecalibrate: () => void
  vboxMm: number
  onVboxMmChange: (value: number) => void
  onSetRejectedFinalPreview?: (enabled: boolean) => boolean | Promise<boolean>
  onOffsetPoint?: (
    side: OpticalMeasurePointSide,
    amount: number
  ) => boolean | Promise<boolean>
  onResetPoint?: (side?: OpticalMeasurePointSide) => boolean | Promise<boolean>
  onOpenSettings: () => void
}

type CandidateDebugLike = {
  confidence?: number
  score?: number
  sampledLuma?: number
  luma?: number
  fitResidual?: number
  usedArcFit?: boolean
}

type ShDebugLike = Omit<ShDebugSummary, 'left' | 'right'> & {
  scale?: ShScaleDebug
  glassesPresenceAccepted?: boolean
  glassesPresence?: GlassesPresenceResult
  quality?: number
  qualityDebug?: SHQualityDebug
  left?: CandidateDebugLike
  right?: CandidateDebugLike
}

const fmtOptional = (value: number | undefined, digits = 2) =>
  value == null ? '-' : fmt(value, digits)

const formatReasons = (reasons: string[] | undefined) =>
  reasons?.length ? reasons.join(', ') : null

function formatRect(
  rect:
    | {
        x: number
        y: number
        width: number
        height: number
      }
    | undefined,
  digits = 1
) {
  if (!rect) return '-'
  return `${fmt(rect.x, digits)}, ${fmt(rect.y, digits)} ${fmt(
    rect.width,
    digits
  )} x ${fmt(rect.height, digits)}`
}

function ContourBoundsRows({
  label,
  eye,
}: {
  label: string
  eye?: SHContourQualityEyeDebug
}) {
  if (!eye) return null

  const candidate = eye.candidate
  const hasBounds =
    candidate.pathBoundsWidthPx != null ||
    candidate.pathBoundsHeightPx != null ||
    candidate.eyeDistancePx != null
  if (!hasBounds) return null

  return (
    <>
      <Row
        label={`${label} BBox`}
        value={`${fmtOptional(candidate.pathBoundsWidthPx, 1)} x ${fmtOptional(
          candidate.pathBoundsHeightPx,
          1
        )} px (${fmtOptional(candidate.widthRatio)} / ${fmtOptional(
          candidate.heightRatio
        )})`}
      />
      <Row
        label={`${label} Huge Limit`}
        value={`Eye ${fmtOptional(
          candidate.eyeDistancePx,
          1
        )} px Max ${fmtOptional(
          candidate.hugeWidthMaxPx,
          1
        )} x ${fmtOptional(candidate.hugeHeightMaxPx, 1)}`}
      />
    </>
  )
}

function VboxInput({
  value,
  onChange,
}: {
  value: number
  onChange: (value: number) => void
}) {
  const [text, setText] = useState(value > 0 ? String(value) : '')

  useEffect(() => {
    setText(value > 0 ? String(value) : '')
  }, [value])

  return (
    <Input
      label="Lens Height (VBOX)"
      labelVariant="inline"
      hideIndicator
      keyboardType="numbers-and-punctuation"
      returnKeyType="done"
      value={text}
      textEnd="mm"
      onChangeText={setText}
      onEndEditing={(e) => {
        const next = e.nativeEvent.text.trim()
        if (!next) {
          onChange(0)
          return
        }
        const parsed = parseFloat(next.replace(',', '.'))
        onChange(Number.isFinite(parsed) && parsed > 0 ? parsed : 0)
      }}
    />
  )
}

function CandidateRows({
  label,
  candidate,
}: {
  label: string
  candidate?: CandidateDebugLike
}) {
  if (!candidate) return null

  const luma = candidate.sampledLuma ?? candidate.luma
  return (
    <>
      <Row
        label={`${label} Candidate`}
        value={`C ${fmtOptional(candidate.confidence)} S ${fmtOptional(
          candidate.score,
          0
        )}`}
      />
      <Row
        label={`${label} Detail`}
        value={`Luma ${fmtOptional(luma, 0)} Fit ${fmtOptional(
          candidate.fitResidual,
          3
        )} ${candidate.usedArcFit ? 'Arc' : 'Raw'}`}
      />
    </>
  )
}

function scaleSourceValue(scale: ShScaleDebug) {
  if (!scale.applied) {
    return scale.rejectionReason ? `PD (${scale.rejectionReason})` : 'PD'
  }

  const vbox = `VBOX ${fmtOptional(scale.providedVboxMm)} mm`
  if (scale.source !== 'vbox-blend') return vbox
  return `${vbox} blended ${fmtOptional((scale.vboxTrust ?? 0) * 100, 0)}%`
}

function ScaleRows({ scale }: { scale?: ShScaleDebug }) {
  if (!scale) return null

  return (
    <>
      <Row label="Scale Source" value={scaleSourceValue(scale)} />
      <Row
        label="Frame Height"
        value={`${fmtOptional(scale.frameHeightPx, 1)} px${
          scale.spanSource && scale.spanSource !== 'both'
            ? ` (${scale.spanSource} span only)`
            : ''
        }`}
      />
      <Row
        label="Scale"
        value={`PD ${fmtOptional(scale.pdMmPerPixel, 4)} / V ${fmtOptional(
          scale.vboxMmPerPixel,
          4
        )}`}
      />
      <Row label="Scale PD" value={`${fmtOptional(scale.pdMm, 2)} mm`} />
      {scale.scaleRatio != null && (
        <Row
          label="Scale Ratio"
          value={`${fmt(scale.scaleRatio, 3)} (trust ${fmtOptional(
            scale.vboxTrust
          )})`}
        />
      )}
      {scale.rawLeft != null && scale.adjustedLeft != null && (
        <Row
          label="Adjustment"
          value={`L ${fmt(scale.adjustedLeft - scale.rawLeft, 2)} / R ${fmt(
            (scale.adjustedRight ?? 0) - (scale.rawRight ?? 0),
            2
          )} mm`}
        />
      )}
    </>
  )
}

const estimateLabels: Record<OpticalMeasureSHEstimateFormula, string> = {
  off: 'Off',
  standard: 'Standard',
  pilot: 'Pilot',
}

function QualityRows({ quality }: { quality?: SHQualityDebug }) {
  if (!quality) return null
  const hardReasons = formatReasons(quality.hardRejectionReasons)
  const softReasons = formatReasons(quality.softRejectionReasons)
  const estimate = quality.estimate
  const estimateLabel = estimateLabels[estimate?.formula || 'off']

  return (
    <>
      <Row
        label="Candidate Quality"
        value={`Q ${fmtOptional(quality.quality)} C ${fmtOptional(
          quality.scores.contour
        )}`}
      />
      <Row
        label="Quality Scores"
        value={`Sym ${fmtOptional(quality.scores.symmetry)} Pose ${fmtOptional(
          quality.scores.pose
        )}`}
      />
      <Row
        label="Scale / Distance"
        value={`Scale ${fmtOptional(quality.scores.scale)} Dist ${fmtOptional(
          quality.scores.distance
        )}`}
      />
      <Row
        label="Contour Scores"
        value={`L ${fmtOptional(quality.scores.leftContour)} / R ${fmtOptional(
          quality.scores.rightContour
        )}`}
      />
      <Row
        label="SH Delta"
        value={`${fmtOptional(quality.measurements.shDeltaMm)} mm`}
      />
      {estimate && (
        <>
          <Row
            label="Estimate"
            value={
              estimate.applied
                ? `${estimateLabel} ${fmtOptional(estimate.estimatedShMm)} mm`
                : `${estimateLabel} (${estimate.reason ?? 'not-applied'})`
            }
          />
          {estimate.applied && (
            <Row
              label="Estimate Quality"
              value={`S ${fmtOptional(estimate.score)} Err ${fmtOptional(
                estimate.errorMm
              )} mm`}
            />
          )}
          {estimate.warning && (
            <Row label="Estimate Warning" value={estimate.warning} />
          )}
        </>
      )}
      {quality.rejectionReason && (
        <Row label="Quality Rejection" value={quality.rejectionReason} />
      )}
      {hardReasons && <Row label="Hard Reject" value={hardReasons} />}
      {softReasons && <Row label="Soft Penalty" value={softReasons} />}
      {quality.eyes?.left && (
        <Row
          label="Left Contour Detail"
          value={`B ${
            quality.eyes.left.candidate.bottomClusterCount
          } W ${fmtOptional(
            quality.eyes.left.candidate.bottomClusterWidthPx,
            1
          )} Lat ${fmtOptional(
            quality.eyes.left.candidate.lateralDistancePx,
            1
          )}`}
        />
      )}
      <ContourBoundsRows label="Left" eye={quality.eyes?.left} />
      {quality.eyes?.right && (
        <Row
          label="Right Contour Detail"
          value={`B ${
            quality.eyes.right.candidate.bottomClusterCount
          } W ${fmtOptional(
            quality.eyes.right.candidate.bottomClusterWidthPx,
            1
          )} Lat ${fmtOptional(
            quality.eyes.right.candidate.lateralDistancePx,
            1
          )}`}
        />
      )}
      <ContourBoundsRows label="Right" eye={quality.eyes?.right} />
    </>
  )
}

function GlassesPresenceRows({
  presence,
}: {
  presence?: GlassesPresenceResult
}) {
  if (!presence) return null

  return (
    <>
      <Row
        label="Glasses Gate"
        value={`${presence.present ? 'YES' : 'NO'} ${
          presence.reason ? `(${presence.reason})` : ''
        }`}
      />
      <Row
        label="Glasses Model"
        value={`${presence.model} ${
          presence.inputSize ? `${presence.inputSize}px` : ''
        }`}
      />
      {presence.probability != null && (
        <Row
          label="Glasses Probability"
          value={`${fmt(presence.probability, 4)} >= ${fmt(
            presence.threshold ?? 0.4,
            2
          )}`}
        />
      )}
      {presence.logit != null && (
        <Row label="Glasses Logit" value={fmt(presence.logit, 4)} />
      )}
      {presence.preprocessing && (
        <Row label="Glasses Input" value={presence.preprocessing} />
      )}
      <Row label="Glasses Crop" value={formatRect(presence.crop)} />
    </>
  )
}

function SHDebugRows({
  debug,
  fallbackPresence,
  title = 'Detection',
}: {
  debug?: ShDebugLike | ShDebug
  fallbackPresence?: GlassesPresenceResult
  title?: string
}) {
  if (!debug) {
    if (!fallbackPresence) return null

    return (
      <View className="gap-1">
        <Typography variant="XS" className="mb-1 text-black/30">
          {title}
        </Typography>
        <GlassesPresenceRows presence={fallbackPresence} />
      </View>
    )
  }

  const rejection =
    debug.measurementRejectionReason ?? debug.rejectionReason ?? null
  const glassesPresence = debug.glassesPresence ?? fallbackPresence

  return (
    <View className="gap-1">
      <Typography variant="XS" className="mb-1 text-black/30">
        {title}
      </Typography>
      <Row label="SH Confidence" value={fmtOptional(debug.confidence)} />
      {debug.quality != null && (
        <Row label="SH Quality" value={fmtOptional(debug.quality)} />
      )}
      {debug.selectedContourType && (
        <Row label="Contour" value={debug.selectedContourType} />
      )}
      {debug.coremlModel && (
        <Row
          label="Core ML Model"
          value={`${debug.coremlModel}${debug.isFinalFrameModel ? ' Final' : ''}`}
        />
      )}
      {debug.cropWidth != null && debug.cropHeight != null && (
        <Row
          label="Core ML Crop"
          value={`${debug.cropWidth} x ${debug.cropHeight}${
            debug.cropScreenWidth != null && debug.cropScreenHeight != null
              ? ` (${debug.cropScreenWidth} x ${debug.cropScreenHeight} pt)`
              : ''
          }`}
        />
      )}
      {debug.measurementAccepted != null && (
        <Row
          label="Accepted Frame"
          value={debug.measurementAccepted ? 'YES' : 'NO'}
        />
      )}
      {debug.glassesPresenceAccepted != null && (
        <Row
          label="Glasses Accepted"
          value={debug.glassesPresenceAccepted ? 'YES' : 'NO'}
        />
      )}
      {rejection && <Row label="Rejection" value={rejection} />}
      <Row
        label="Candidates"
        value={`${debug.acceptedCandidateCount ?? 0} accepted / ${
          debug.otherContourCount ?? 0
        } other`}
      />
      <Row
        label="Bright Rejected"
        value={String(debug.rejectedBrightContourCount ?? 0)}
      />
      <ScaleRows scale={debug.scale} />
      <GlassesPresenceRows presence={glassesPresence} />
      <QualityRows quality={debug.qualityDebug} />
      <CandidateRows label="Left" candidate={debug.left} />
      <CandidateRows label="Right" candidate={debug.right} />
    </View>
  )
}

function CalibrationRows({
  calibration,
}: {
  calibration?: {
    contrastAdjustment: number
    detectsDarkOnLight: boolean
    score: number
  }
}) {
  if (!calibration) return null

  return (
    <View className="gap-1">
      <Typography variant="XS" className="mb-1 text-black/30">
        Calibration
      </Typography>
      <Row
        label="Contrast"
        value={`${fmt(calibration.contrastAdjustment, 2)} ${
          calibration.detectsDarkOnLight ? 'Dark' : 'Light'
        }`}
      />
      <Row label="Score" value={fmt(calibration.score, 0)} />
    </View>
  )
}

function ResultRows({
  label,
  result,
  emptyText,
}: {
  label: string
  result?: { left: number; right: number } | null
  emptyText?: string
}) {
  if (!result) {
    return (
      <Typography variant="XS" className="text-black/40">
        {emptyText ?? 'No result'}
      </Typography>
    )
  }

  return (
    <View className="gap-1">
      <Typography variant="XS" className="mb-1 text-black/30">
        {label}
      </Typography>
      <Row label="SH Left" value={`${fmt(result.left, 2)} mm`} />
      <Row label="SH Right" value={`${fmt(result.right, 2)} mm`} />
    </View>
  )
}

function AdjustmentControls({
  adjustment,
  onOffsetPoint,
  onResetPoint,
}: {
  adjustment?: OpticalMeasurePointAdjustmentState
  onOffsetPoint?: (
    side: OpticalMeasurePointSide,
    amount: number
  ) => boolean | Promise<boolean>
  onResetPoint?: (side?: OpticalMeasurePointSide) => boolean | Promise<boolean>
}) {
  const [isUpdating, setIsUpdating] = useState(false)

  if (!adjustment?.active || adjustment.type !== 'sh-bottom') return null
  if (!onOffsetPoint || !onResetPoint) return null

  const runCommand = async (command: () => boolean | Promise<boolean>) => {
    setIsUpdating(true)
    try {
      await command()
    } finally {
      setIsUpdating(false)
    }
  }

  const sideRows: Array<{ side: OpticalMeasurePointSide; label: string }> = [
    { side: 'left', label: 'Left Bottom' },
    { side: 'right', label: 'Right Bottom' },
  ]

  return (
    <View className="gap-2">
      <Typography variant="XS" className="mb-1 text-black/30">
        Bottom Edge Adjustment
      </Typography>
      {sideRows.map(({ side, label }) => (
        <View key={side} className="gap-1">
          <Row
            label={label}
            value={
              adjustment.points[side]
                ? `${fmt(adjustment.points[side].x, 1)}, ${fmt(
                    adjustment.points[side].y,
                    1
                  )} px`
                : '-'
            }
          />
          <View className="flex-row gap-2">
            <Button
              className="flex-1"
              size="sm"
              variant="outlined"
              label="-0.1 mm"
              disabled={isUpdating}
              onPress={() => void runCommand(() => onOffsetPoint(side, -0.1))}
            />
            <Button
              className="flex-1"
              size="sm"
              variant="outlined"
              label="+0.1 mm"
              disabled={isUpdating}
              onPress={() => void runCommand(() => onOffsetPoint(side, 0.1))}
            />
            <Button
              className="flex-1"
              size="sm"
              variant="outlined"
              label="Reset"
              disabled={isUpdating}
              onPress={() => void runCommand(() => onResetPoint(side))}
            />
          </View>
        </View>
      ))}
      <Button
        size="sm"
        variant="outlined"
        label="Reset Both"
        disabled={isUpdating}
        onPress={() => void runCommand(() => onResetPoint())}
      />
    </View>
  )
}

export function SHTab({
  phase,
  capturedCount,
  targetCount,
  progress,
  onStart,
  onStop,
  onClear,
  onRecalibrate,
  vboxMm,
  onVboxMmChange,
  onSetRejectedFinalPreview,
  onOffsetPoint,
  onResetPoint,
  onOpenSettings,
}: Props) {
  const data = useShFaceData()
  const [isRejectedFinalPreview, setIsRejectedFinalPreview] = useState(false)

  const isIdle = phase === 'idle'
  const isComplete = phase === 'complete'
  const isMeasuring = phase !== 'idle' && phase !== 'complete'
  const isProcessingFinalFrame =
    isComplete &&
    data?.freeze?.active &&
    data.freeze.mode === 'sh' &&
    data.freeze.processing
  const frozenBestResult =
    isComplete &&
    !isProcessingFinalFrame &&
    data?.freeze?.active &&
    data.freeze.source === 'latest-good' &&
    data.freeze.mode === 'sh' &&
    data.sh
      ? data.sh
      : null
  const completeDebug = frozenBestResult ? data?.shDebug : undefined
  const rejectedFinalDebug = data?.finalRerunDebug
  const hasRejectedFinalDebug = rejectedFinalDebug != null

  useEffect(() => {
    if (hasRejectedFinalDebug || !isRejectedFinalPreview) return
    setIsRejectedFinalPreview(false)
    void onSetRejectedFinalPreview?.(false)
  }, [
    hasRejectedFinalDebug,
    isRejectedFinalPreview,
    onSetRejectedFinalPreview,
  ])

  const handleRejectedFinalPreviewPress = async () => {
    const nextPreview = !isRejectedFinalPreview
    const ok = await onSetRejectedFinalPreview?.(nextPreview)
    if (ok === false) return
    setIsRejectedFinalPreview(nextPreview)
  }

  return (
    <View className="flex-1">
      <ScrollView
        className="flex-1"
        contentContainerClassName="gap-3 px-4 py-3"
      >
        <PanelBox className="gap-3">
          <View className="flex-row justify-end">
            <Button
              size="sm"
              variant="outlined"
              iconStart="settings"
              onPress={onOpenSettings}
            />
          </View>

          <VboxInput value={vboxMm} onChange={onVboxMmChange} />

          {!isIdle && (
            <View className="gap-1">
              <Row
                label={isProcessingFinalFrame ? 'Finalizing Result' : 'Captured Frames'}
                value={`${capturedCount} / ${targetCount}`}
              />
              <ProgressBar progress={progress} isPulsing={isProcessingFinalFrame} />
            </View>
          )}

          {isComplete ? (
            <>
              {!isProcessingFinalFrame && (
                <>
                  <ResultRows
                    label="Best Frame Result"
                    result={frozenBestResult}
                    emptyText="No Valid Frozen SH Result"
                  />
                  <AdjustmentControls
                    adjustment={data?.shAdjustment}
                    onOffsetPoint={onOffsetPoint}
                    onResetPoint={onResetPoint}
                  />
                  <SHDebugRows
                    debug={completeDebug}
                    fallbackPresence={data?.glassesPresence}
                  />
                  <SHDebugRows
                    title="Rejected Final Frame"
                    debug={rejectedFinalDebug}
                  />
                  {rejectedFinalDebug && onSetRejectedFinalPreview && (
                    <Button
                      size="sm"
                      variant={isRejectedFinalPreview ? 'filled' : 'outlined'}
                      label={
                        isRejectedFinalPreview
                          ? 'Hide Rejected Preview'
                          : 'Preview Rejected'
                      }
                      onPress={() => void handleRejectedFinalPreviewPress()}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {data?.sh ? (
                <View className="gap-1">
                  <Typography variant="XS" className="mb-1 text-black/30">
                    Live
                  </Typography>
                  <Row label="SH Left" value={`${fmt(data.sh.left, 2)} mm`} />
                  <Row label="SH Right" value={`${fmt(data.sh.right, 2)} mm`} />
                </View>
              ) : isIdle ? (
                <Typography variant="XS" className="text-black/40">
                  {data?.calibrating
                    ? 'Calibrating contrast…'
                    : data
                    ? 'Frame rim not detected — check lighting'
                    : 'Waiting for face…'}
                </Typography>
              ) : null}
              <SHDebugRows
                debug={data?.shDebug}
                fallbackPresence={data?.glassesPresence}
              />
            </>
          )}

          <CalibrationRows calibration={data?.calibration} />
        </PanelBox>
      </ScrollView>

      <View className="h-16 flex-row gap-2 px-4 py-3">
        <Button
          className="flex-1"
          size="sm"
          variant="filled"
          label="Start"
          onPress={onStart}
          disabled={!isIdle}
        />
        <Button
          className="flex-1"
          size="sm"
          variant="outlined"
          label="Calibrate"
          onPress={onRecalibrate}
          disabled={!isIdle}
        />
        <Button
          className="flex-1"
          size="sm"
          variant="outlined"
          label="Cancel"
          onPress={onStop}
          disabled={!isMeasuring}
        />
        <Button
          className="flex-1"
          size="sm"
          variant="outlined"
          label="Clear"
          onPress={onClear}
          disabled={!isComplete}
        />
      </View>
    </View>
  )
}
